<template>
  <b-modal
    v-model="editorShown"
    centered
    size="md-lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
      >
        <h5 class="modal-title">{{ formTitle }}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity: 1 !important"
          @click="$emit('onHidden')"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div class="text-right mt-1 mr-2">
        <b-button @click="$emit('onHidden')" variant="secondary">
          Cancel
        </b-button>
        <b-button
          :disabled="
            hasSelectedInvoiceWithZeroAmount ||
              !hasSelectedInvoice ||
              hasInvoiceErrors ||
              (selectedPaymentMethod === 'online' && !form.card)
          "
          class="ml-2"
          @click="submitPayment"
          variant="primary"
        >
          Add Payment
        </b-button>
      </div>
    </template>
    <b-row>
      <b-col>
        <div class="font-weight-bold mb-2 lower-font-size">
          Invoices and payment amount:
        </div>
        <div
          class="
            invoice-container
            d-flex
            flex-column
            align-items-center
          "
        >
          <b-table
            class="bg-white"
            head-variant="light"
            :items="form.invoices"
            :fields="fields"
            style="font-size: 14px"
          >
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.colWidth }"
              />
            </template>
            <template #cell(selected)="row">
              <b-form-checkbox v-model="row.item.selected" />
            </template>
            <template #cell(invoice)="row">
              {{ `Invoice: ${row.item.invoiceRef}` }}
            </template>
            <template #cell(details)="row">
              <span v-if="row.item.details.length > 0">
                {{ row.item.details.length }} {{ row.item.details.length === 1? 'Item' : 'Items' }}:
              </span>
              <span v-else> This invoice has no services </span>
              <div v-for="(item, index) in row.item.details" :key="index">
                {{ `(${formatDateOnly(item.date)})` }} {{ item.description }}
              </div>
            </template>
            <template #cell(balance)="row">
              <div class="text-right">
                {{ toCurrencyValue(row.item.balance) }}
              </div>
            </template>
            <template #cell(amount)="row">
              <b-form-group
                class="text-center ml-4"
                :state="formErrors.invoices.length > 0 ? false : null"
                :invalid-feedback="
                  formErrors.invoices
                    .map(error => {
                      if (error.index === row.index) {
                        return error.message;
                      }
                    })
                    .join(', ')
                "
              >
                <b-input-group size="sm" prepend="$">
                  <b-form-input
                    class="text-right"
                    v-model="row.item.amount"
                    type="number"
                    required
                    :id="'input-amount-' + row.index"
                    :disabled="!row.item.selected"
                    size="sm"
                  />
                </b-input-group>
              </b-form-group>
            </template>
          </b-table>
          <div class="d-flex justify-content-between align-items-center w-100">
            <div
              class="text-danger total-amount-errors"
            >
              {{ formErrors.totalAmount.join(", ") }}
            </div>
            <div class="d-flex justify-content-end my-4">
              <div style="margin-right: 38px">Total amount:</div>
              <div style="margin-right: 42px" class="font-weight-bold">
                {{ toCurrencyValue(paymentAmount) }}
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col>
        <div class="font-weight-bold mb-2 mt-4 lower-font-size">
          Payment method:
        </div>
        <div class="w-100 lower-font-size">
          <b-form-group
            :state="formErrors.card.length == 0 ? null : false"
            :invalid-feedback="formErrors.card.join(', ')"
          >
            <b-form-radio-group stacked v-model="selectedPaymentMethod">
              <div
                v-show="systemSetting.payaEnable"
                class="p-2"
                :style="
                  selectedPaymentMethod === 'online'
                    ? 'background-color: #f1f1f1'
                    : ''
                "
              >
                <b-form-radio value="online">Credit/Debit Card</b-form-radio>
              </div>
              <div
                class="
                  d-flex
                  justify-content-start
                  align-items-center
                  payment-method-container
                  p-2
                "
                v-if="
                  selectedPaymentMethod === 'online' && systemSetting.payaEnable
                "
              >
                <patient-payment-methods
                  class="m-2"
                  :patientId="this.patientId"
                  v-model="form.card"
                ></patient-payment-methods>
              </div>
              <div
                class="p-2"
                :style="
                  selectedPaymentMethod === 'cash'
                    ? 'background-color: #f1f1f1'
                    : ''
                "
              >
                <b-form-radio value="cash"> Cash</b-form-radio>
              </div>
              <div
                v-if="selectedPaymentMethod === 'cash'"
                class="
                  d-flex
                  justify-content-start
                  align-items-center
                  payment-method-container
                  p-2
                "
              >
                <b-form-group label="Payment Date">
                  <date-picker size="sm" v-model="cashPaymentDate" />
                </b-form-group>
              </div>
              <div
                class="p-2"
                :style="
                  selectedPaymentMethod === 'check'
                    ? 'background-color: #f1f1f1'
                    : ''
                "
              >
                <b-form-radio value="check"> Check</b-form-radio>
              </div>
              <div
                v-if="selectedPaymentMethod === 'check'"
                class="
                  d-flex
                  justify-content-start
                  align-items-center
                  payment-method-container
                  p-2
                "
              >
                <b-form-group label="Payment Date">
                  <date-picker size="sm" v-model="checkPaymentDate" />
                </b-form-group>

                <b-form-group label="Check Number" class="ml-5">
                  <b-form-input size="sm" v-model="checkNumber"></b-form-input>
                </b-form-group>
              </div>
              <div
                class="p-2"
                :style="
                  selectedPaymentMethod === 'external'
                    ? 'background-color: #f1f1f1'
                    : ''
                "
              >
                <b-form-radio value="external">External Method</b-form-radio>
              </div>
              <div
                v-if="selectedPaymentMethod === 'external'"
                class="
                  d-flex
                  justify-content-start
                  align-items-center
                  payment-method-container
                  p-2
                "
              >
                <b-form-group label="Payment Date">
                  <date-picker size="sm" v-model="externalPaymentDate" />
                </b-form-group>
                <b-form-group label="External Payment Method" class="ml-5">
                  <b-form-input
                    size="sm"
                    v-model="externalPaymentMethod"
                  ></b-form-input>
                </b-form-group>
              </div>
            </b-form-radio-group>
          </b-form-group>
        </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import paymentServer from "../../../server/payment";
import invoiceServer from "../../../server/invoice";
import patientServer from "../../../server/patient";
import { validateRequired } from "../../../helpers/validation";
import DatePicker from "../../controls/DatePicker.vue";
import PatientPaymentMethods from "../../episode_of_care/PatientPaymentMethods.vue";
import { formatDateOnlyMysql } from "../../../helpers/date";
import moment from "moment";

export default {
  name: "PaymentAddEditModal",
  props: [
    "patientId",
    "paymentId",
    "invoiceId",
    "formTitle",
    "transactionType"
  ],
  components: {
    DatePicker,
    PatientPaymentMethods
  },
  data() {
    return {
      form: {
        patientId: null,
        invoices: [],
        paymentMethod: null,
        card: null
      },
      formErrors: {
        invoices: [],
        paymentMethod: [],
        paymentType: [],
        card: [],
        totalAmount: []
      },
      patient: null,
      items: [],
      fields: [
        {
          key: "selected",
          label: "",
          thClass: "color-custom-dark-grey",
          colWidth: "47px"
        },
        { key: "invoice", label: "Invoice", thClass: "color-custom-dark-grey" },
        { key: "details", label: "Details", thClass: "color-custom-dark-grey" },
        {
          key: "balance",
          label: "Balance",
          thClass: "color-custom-dark-grey text-right",
          colWidth: "100px"
        },
        {
          key: "amount",
          label: "Amount",
          thClass: "color-custom-dark-grey text-right",
          colWidth: "165px"
        }
      ],
      selectedPaymentMethod: "online",
      cashPaymentDate: formatDateOnlyMysql(new Date()),
      checkPaymentDate: formatDateOnlyMysql(new Date()),
      checkNumber: "",
      externalPaymentDate: formatDateOnlyMysql(new Date()),
      externalPaymentMethod: ""
    };
  },
  async mounted() {
    this.fetchPatient();
    const response = await invoiceServer.query({
      episodeOfCareId: null,
      patientId: this.patientId !== undefined ? [this.patientId] : [],
      createdById: null,
      searchPhrase: null,
      dateFrom: null,
      dateTo: null,
      page: 1,
      pageSize: 50,
      orderBy: null,
      orderByDescending: false,
      unpaid: true
    });
    this.form.invoices = response.invoices
        .sort((a, b) => Date.parse(a.lineItems?.[0]?.date ?? 0) - Date.parse(b.lineItems?.[0]?.date ?? 0))
        .map(({ id, refNumber, lineItems, totalAmount, amountPaid }) => ({
        id,
        invoiceRef: refNumber,
        details: lineItems.sort((a, b) => Date.parse(a.date) - Date.parse(b.date)),
        balance: totalAmount - amountPaid,
        amount:
          this.invoiceId !== null && id === this.invoiceId
            ? totalAmount - amountPaid
            : "0",
        selected:
          this.invoiceId !== null && id === this.invoiceId ? true : false
      }))
      .filter(invoice => invoice.balance > 0);
    if (this.paymentId) {
      const invoices = await invoiceServer.query({
        patientId: this.patientId
      });
      const payment = await paymentServer.get(this.paymentId);
      this.form = {
        ...this.form,
        patientId: this.patientId,
        invoices: invoices,
        paymentMethod: payment.paymentMethod
      };
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    validatePaymentSubmit() {
      let withoutErrors = true;

      this.formErrors.paymentType = [];
      if (!validateRequired(this.selectedPaymentMethod)) {
        this.formErrors.paymentType.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.card = [];
      if (this.isCardExpired() && this.selectedPaymentMethod === "online") {
        this.formErrors.card.push("The card has already expired");
        withoutErrors = false;
      }

      this.formErrors.totalAmount = [];
      if (this.paymentAmount < 0.01) {
        this.formErrors.totalAmount.push(
          "Total Amount can't be less than 0.01"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    },
    isCardExpired() {
      if (!this.form.card) return false;

      const currentYear = parseInt(
        moment()
          .year()
          .toString()
          .slice(-2)
      );
      const currentMonth = moment().month() + 1;

      const cardExpirationDate = this.form.card.expDate.trim();
      const cardExpirationMonth = parseInt(cardExpirationDate.slice(0, 2));
      const cardExpirationYear = parseInt(
        cardExpirationDate.slice(2, cardExpirationDate.length)
      );

      const isExpirationYearBelow = cardExpirationYear < currentYear;
      const isSameYear = cardExpirationYear === currentYear;
      const isExpirationMonthBelow = cardExpirationMonth < currentMonth;

      return isExpirationYearBelow || (isSameYear && isExpirationMonthBelow);
    },
    async submitPayment(modalEvent) {
      modalEvent.preventDefault();
      const result = await this.validatePaymentSubmit();
      const paymentData = {
        patientId: this.patientId,
        type: this.transactionType || "Payment",
        method: this.selectedPaymentMethod,
        paymentDate: this.getPaymentDate(),
        checkNumber: this.checkNumber,
        externalMethod: this.externalPaymentMethod,
        totalAmount: this.paymentAmount,
        pendingAllocatedAmount: 0,
        refundableAmount: 0,
        invoices: this.form.invoices
          .filter(invoice => invoice.selected)
          .map(invoice => ({
            ...invoice,
            amount: parseFloat(invoice.amount),
            refNumber: invoice.invoiceRef
          })),
        cardInfokey: this.form.card?.ccInfoKey
      };
      if (result) {
        this.$dialog
          .confirm(
            {
              title: `Are you sure you want to submit a payment for  <strong>${this.toCurrencyValue(
                this.paymentAmount
              )}</strong>?`
            },
            {
              view: "confirm",
              options: [{ value: true, label: "Yes" }]
            }
          )
          .then(async () => {
            const response = await paymentServer.create(paymentData);
            if (response.id) {
              this.showAlertSuccess();
              this.$emit("success");
              this.showSuccessPayment();
              this.editorShown = false;
            }
          })
          .catch(async err => {
            const data = err?.response?.data;
            if (data?.response?.result === "110") {
              await this.$dialog
                .confirm(
                  {
                    title:
                      "Possible duplicate payment, do you want to continue?"
                  },
                  {
                    view: "confirm",
                    options: [{ value: true, label: "Yes" }]
                  }
                )
                .then(async () => {
                  const response = await paymentServer.create({
                    ...paymentData,
                    forceTransaction: "True"
                  });
                  if (response.id) {
                    this.showAlertSuccess();
                    this.$emit("success");
                    this.showSuccessPayment();
                    this.editorShown = false;
                  }
                })
                .catch(err => {
                  const data = err?.response?.data;
                  this.showErrorPayment(data);
                });
            } else {
              this.showErrorPayment(data);
            }
          });
      }
    },
    showSuccessPayment() {
      this.$dialog.confirm(
        {},
        {
          view: "payment-result",
          data: {
            patient: this.formatClient(
              this.patient.firstName ?? "",
              this.patient.middleName ?? "",
              this.patient.preferredName ?? "",
              this.patient.lastName ?? ""
            ),
            amount: this.toCurrencyValue(this.paymentAmount),
            method: this.formatedPaymentMethod
          },
          success: true
        }
      );
    },

    showErrorPayment(data) {
      this.$dialog.confirm(
        {},
        {
          view: "payment-result",
          data: this.normalizeErrorData(data),
          success: false
        }
      );
    },
    normalizeErrorData(data) {
      if (typeof data === "string") {
        return { message: data };
      }
      if (data && typeof data === "object") {
        return data;
      }
      return {
        message:
          "An unexpected error occurred while processing the payment. Please try again."
      };
    },
    async fetchPatient() {
      this.patient = await patientServer.get(this.patientId);
    },
    getPaymentDate() {
      let date;
      switch (this.selectedPaymentMethod) {
        case "cash":
          date = this.cashPaymentDate;
          break;
        case "check":
          date = this.checkPaymentDate;
          break;
        case "external":
          date = this.externalPaymentDate;
          break;
        default:
          date = new Date();
          break;
      }
      return date;
    }
  },
  computed: {
    ...mapState("layout", {
      systemSetting: state => state.systemSetting
    }),
    ...mapGetters("layout", ["datesNonWorking", "getCurrentAdjustedDateTime"]),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    formatedPaymentMethod() {
      switch (this.selectedPaymentMethod) {
        case "online":
          return "Credit/Debit Card";
        case "cash":
          return "Cash";
        case "check":
          return "Check";
        case "external":
          return "External Method";
        default:
          return "";
      }
    },
    paymentAmount() {
      return this.form.invoices.reduce((accumulator, invoice) => {
        if (invoice.selected && !isNaN(parseFloat(invoice.amount))) {
          return accumulator + parseFloat(invoice.amount);
        } else {
          return accumulator;
        }
      }, 0);
    },
    hasSelectedInvoiceWithZeroAmount() {
      return this.form.invoices.some(
        invoice =>
          invoice.selected && (invoice.amount === "0" || invoice.amount === "")
      );
    },
    hasSelectedInvoice() {
      return this.form.invoices.some(invoice => invoice.selected);
    },
    hasInvoiceErrors() {
      return this.formErrors.invoices.length > 0;
    },
    noPaymentTypeFilled() {
      return true;
    }
  },
  watch: {
    "form.invoices": {
      handler(newVal) {
        this.formErrors.invoices = [];
        newVal.forEach((invoice, index) => {
          if (invoice.amount > invoice.balance) {
            const errorMessage =
              "Amount can't be greater than: " +
              `${this.toCurrencyValue(invoice.balance)}`;
            const errorObject = {
              index: index,
              message: errorMessage
            };
            this.formErrors.invoices.push(errorObject);
          }
        });
      },
      deep: true
    },
    selectedPaymentMethod(newValue) {
      if (newValue !== "online") {
        this.form.card = null;
      }
    }
  }
};
</script>

<style scoped>
.invoice-container {
  background-color: #f1f1f1;
  max-height: 500px;
  overflow: auto;
}

.b-table {
  width: 97%;
  margin: 15px;
  margin-bottom: 0px;
}

.total-amount-errors {
  max-width: 50%;
  padding-left: 2rem;
}
</style>

<style lang="scss" scoped>
.payment-method-container {
  padding: 10px;
  background-color: #f1f1f1;
  width: 100%;
}

label > .custom-control-label {
  background-color: red !important;
}
</style>
